<script lang="ts">
  /**
   * Compact Lanes card. Visual language follows Focus chips + Board task rows,
   * but this component is intentionally standalone so it can diverge.
   *
   * Title is prominent; at most 3 incomplete tasks. Title opens the rail;
   * task click is delegated so the parent can open TaskDetailPopover.
   * Task rows keep a pointer cursor and hover wash so they stay distinct from
   * the draggable card chrome.
   */
  import type { Project, Task, TaskPriority } from '../lib/types';
  import { categoryIcons, statusAccentColors } from '../lib/utils';
  import { openProjectRail } from '../lib/stores/ui';
  import { subscribeToCachedTasks } from '../lib/stores/projectTasksCache';
  import { getTaskCounts, subscribeToProjectTaskCounts } from '../lib/useProjectTaskCounts';
  import {
    previewTasksFromCached,
    previewTasksFromIncomplete,
    type TaskPreview,
  } from '../lib/projectLanesUtils';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    project: Project;
    onTaskClick?: (task: TaskPreview, event: MouseEvent | KeyboardEvent) => void;
    draggable?: boolean;
    dragging?: boolean;
    onDragStart?: (event: DragEvent) => void;
    onDragEnd?: () => void;
  }

  let {
    project,
    onTaskClick,
    draggable = false,
    dragging = false,
    onDragStart,
    onDragEnd,
  }: Props = $props();
  let suppressClick = false;
  // No HTML5 drag on phones (long-press would start a half-working drag); the
  // project rail's ⋯ has the tap alternatives (plans/mobile-v2.md §3.5).
  let canDrag = $derived(draggable && !$viewport.isMobile);

  let taskCounts = $state({ total: 0, done: 0, todo: 0 });
  let cachedTasks = $state<Task[]>([]);
  let cacheFetched = $state(false);

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: projectTasksCache / task counts pub/sub
  $effect(() => {
    taskCounts = getTaskCounts(project);
    const unsubCounts = subscribeToProjectTaskCounts(project, (counts) => {
      taskCounts = counts;
    });
    const unsubTasks = subscribeToCachedTasks(project.id, (cached) => {
      cachedTasks = cached.tasks;
      cacheFetched = cached.fetched;
    });
    return () => {
      unsubCounts();
      unsubTasks();
    };
  });

  let previewTasks = $derived(
    cacheFetched
      ? previewTasksFromCached(cachedTasks)
      : previewTasksFromIncomplete(project.incompleteTasks),
  );

  let extraCount = $derived(Math.max(0, taskCounts.todo - previewTasks.length));
  let showTasks = $derived(previewTasks.length > 0);
  let hasTasks = $derived(taskCounts.total > 0);

  const priorityBorderColors: Record<TaskPriority, string> = {
    none: '#d1d5db',
    low: '#60a5fa',
    medium: '#fb923c',
    high: '#ef4444',
  };

  function handleCardClick() {
    if (suppressClick) {
      suppressClick = false;
      return;
    }
    openProjectRail(project.id);
  }

  function handleActivateKey(event: KeyboardEvent, action: () => void) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  }

  function handleDragStart(event: DragEvent) {
    suppressClick = true;
    onDragStart?.(event);
  }

  function handleDragEnd() {
    window.setTimeout(() => {
      suppressClick = false;
    }, 0);
    onDragEnd?.();
  }

  function handleTaskClick(task: TaskPreview, event: MouseEvent | KeyboardEvent) {
    event.stopPropagation();
    onTaskClick?.(task, event);
  }
</script>

<div
  class="preview-card {statusAccentColors[project.status]}"
  class:is-draggable={canDrag}
  class:dragging
  role="article"
  draggable={canDrag}
  ondragstart={canDrag ? handleDragStart : undefined}
  ondragend={canDrag ? handleDragEnd : undefined}
>
  <!-- Divs, not <button>s: native buttons swallow HTML5 drag from a parent card. -->
  <div
    class="card-header"
    role="button"
    tabindex="0"
    aria-label="Open {project.title}"
    onclick={handleCardClick}
    onkeydown={(event) => handleActivateKey(event, handleCardClick)}
  >
    <span class="card-icon" aria-hidden="true">{categoryIcons[project.category]}</span>
    {#if project.isPriority}
      <span class="card-star" title="Priority">★</span>
    {/if}
    <span class="card-title">{project.title}</span>
    {#if hasTasks}
      <span class="card-count" class:all-done={taskCounts.done === taskCounts.total}>
        {taskCounts.done}/{taskCounts.total}
      </span>
    {/if}
  </div>

  {#if showTasks}
    <ul class="task-list">
      {#each previewTasks as task (task.id)}
        <li>
          <div
            class="task-row"
            role="button"
            tabindex="0"
            aria-label="Open task {task.title}"
            onclick={(event) => handleTaskClick(task, event)}
            onkeydown={(event) => handleActivateKey(event, () => handleTaskClick(task, event))}
          >
            <span
              class="task-checkbox"
              style="border-color: {priorityBorderColors[task.priority]}"
              aria-hidden="true"
            ></span>
            <span class="task-title">{task.title}</span>
          </div>
        </li>
      {/each}
    </ul>
    {#if extraCount > 0}
      <div class="more-count">+{extraCount} more</div>
    {/if}
  {/if}
</div>

<style>
  .preview-card.is-draggable,
  .preview-card.is-draggable .card-header {
    cursor: grab;
  }

  .preview-card.is-draggable:active {
    cursor: grabbing;
  }

  /* Tasks stay clickable; drag starts from the header / card chrome. */
  .preview-card.is-draggable .task-row {
    cursor: pointer;
  }

  .preview-card.dragging {
    opacity: 0.45;
  }

  .preview-card {
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    border-radius: 0.25rem;
    overflow: hidden;
    transition: box-shadow 0.15s;
  }

  .preview-card:hover,
  .preview-card:focus-within {
    background: #f3f4f6;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .preview-card.status-planned {
    --chip-border-color: #93c5fd;
  }
  .preview-card.status-active {
    --chip-border-color: #fcd34d;
  }
  .preview-card.status-almost {
    --chip-border-color: #fdba74;
  }
  .preview-card.status-done {
    --chip-border-color: #86efac;
  }

  .card-header {
    display: flex;
    align-items: flex-start;
    gap: 0.25rem;
    width: 100%;
    min-width: 0;
    min-height: 2.5rem;
    box-sizing: border-box;
    padding: 0.3125rem 0.375rem;
    margin: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
  }

  .card-header:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: -2px;
  }

  .card-icon {
    flex-shrink: 0;
    font-size: 0.625rem;
    line-height: 1.3;
  }

  .card-star {
    flex-shrink: 0;
    color: #facc15;
    font-size: 0.5625rem;
    line-height: 1.3;
  }

  .card-title {
    flex: 1;
    min-width: 0;
    font-size: 0.75rem;
    font-weight: 650;
    line-height: 1.25;
    color: #1f2937;
    letter-spacing: -0.01em;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  .card-count {
    flex-shrink: 0;
    margin-top: 0.0625rem;
    font-size: 0.5rem;
    font-weight: 500;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
  }

  .card-count.all-done {
    color: #16a34a;
  }

  .task-list {
    list-style: none;
    margin: 0;
    padding: 0.25rem 0.375rem 0.3125rem;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
    border-top: 1px solid #f3f4f6;
    background: #fafafa;
  }

  .task-row {
    display: flex;
    align-items: center;
    gap: 0.3125rem;
    width: 100%;
    min-width: 0;
    padding: 0.125rem 0.25rem;
    margin: 0;
    border: none;
    border-radius: 0.1875rem;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
  }

  .task-row:hover {
    background: rgba(59, 130, 246, 0.08);
  }

  .task-row:hover .task-title,
  .task-row:focus-visible .task-title {
    color: #1f2937;
  }

  .task-row:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: -2px;
  }

  .task-checkbox {
    width: 0.6875rem;
    height: 0.6875rem;
    flex-shrink: 0;
    border: 1.5px solid #d1d5db;
    border-radius: 0.1875rem;
    background: white;
  }

  .task-title {
    min-width: 0;
    font-size: 0.625rem;
    font-weight: 450;
    line-height: 1.3;
    color: #4b5563;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .more-count {
    padding: 0 0.375rem 0.3125rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #9ca3af;
    background: #fafafa;
  }
</style>
